import React, { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer,
} from 'recharts';
import { AppLayout } from '../components/layout/AppLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, Badge, Skeleton, EmptyState, Button, StatCard } from '../components/common';
import { analyticsAPI, emailsAPI } from '../services/api';
import { useAsync, useMediaQuery } from '../hooks';
import { timeAgo, formatDateTime, statusColor, truncateUrl, deviceIcon } from '../utils/helpers';
import toast from 'react-hot-toast';

const statusVariant = { delivered: 'green', sent: 'cyan', failed: 'red', bounced: 'amber' };

const EventRow = ({ event }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: 14,
    padding: '12px 0', borderBottom: '1px solid var(--border)',
  }}>
    {/* Type indicator */}
    <div style={{
      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '0.75rem',
      background: event.eventType === 'open' ? 'var(--cyan-dim)' : 'var(--amber-dim)',
      border: `1px solid ${event.eventType === 'open' ? 'rgba(0,225,200,0.3)' : 'rgba(245,166,35,0.3)'}`,
      color: event.eventType === 'open' ? 'var(--cyan)' : 'var(--amber)',
    }}>
      {event.eventType === 'open' ? '👁' : '🔗'}
    </div>

    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          {event.eventType === 'open' ? 'Email Opened' : 'Link Clicked'}
        </span>
        {event.isBot && <Badge variant="red">bot</Badge>}
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {event.location?.city && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            📍 {event.location.city}, {event.location.country}
          </span>
        )}
        {event.device?.browser && (
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            {deviceIcon(event.device.deviceType)} {event.device.browser} / {event.device.os}
          </span>
        )}
        {event.originalUrl && (
          <span style={{ fontSize: '0.72rem', color: 'var(--amber)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
            → {truncateUrl(event.originalUrl)}
          </span>
        )}
      </div>
    </div>

    <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', flexShrink: 0, paddingTop: 2 }}>
      {timeAgo(event.timestamp)}
    </span>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-light)', borderRadius: 8, padding: '8px 12px', fontSize: '0.72rem', fontFamily: 'var(--font-mono)' }}>
      <p style={{ color: 'var(--text-muted)', marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></p>)}
    </div>
  );
};

export const EmailDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const fetchDetail   = useCallback(() => analyticsAPI.email(id).then((r) => r.data.data), [id]);
  const fetchTimeline = useCallback(() => analyticsAPI.timeline(id).then((r) => r.data.data), [id]);
  const fetchDevices  = useCallback(() => analyticsAPI.devices(id).then((r) => r.data.data), [id]);

  const { data, loading }         = useAsync(fetchDetail);
  const { data: timeline }        = useAsync(fetchTimeline);
  const { data: devices }         = useAsync(fetchDevices);

  const handleDelete = async () => {
    if (!window.confirm('Delete this email record?')) return;
    try { await emailsAPI.delete(id); toast.success('Deleted'); navigate('/emails'); }
    catch { toast.error('Delete failed'); }
  };

  const email       = data?.email;
  const openEvents  = data?.openEvents  || [];
  const clickEvents = data?.clickEvents || [];
  const allEvents   = [...openEvents, ...clickEvents].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  // Build chart data from timeline
  const chartData = (timeline || []).reduce((acc, item) => {
    const key = item._id?.date;
    if (!key) return acc;
    const existing = acc.find((d) => d.time === key);
    if (existing) { existing[item._id.eventType] = (existing[item._id.eventType] || 0) + item.count; }
    else          { acc.push({ time: key.slice(11, 16), [item._id.eventType]: item.count }); }
    return acc;
  }, []).slice(-24);

  // Device breakdown
  const deviceData = (devices || []).map((d) => ({ type: d._id || 'unknown', count: d.count }));

  if (loading) {
    return (
      <AppLayout>
        <PageHeader title="Email Detail" />
        <div style={{ padding: isMobile ? '16px' : '24px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Skeleton height={80} />
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4,1fr)', gap: 16 }}>{Array(4).fill(0).map((_, i) => <Skeleton key={i} height={90} />)}</div>
        </div>
      </AppLayout>
    );
  }

  if (!email) {
    return (
      <AppLayout>
        <PageHeader title="Email Detail" />
        <EmptyState icon="❌" title="Email not found" description="This email may have been deleted." action={<Button onClick={() => navigate('/emails')}>Back to Emails</Button>} />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageHeader
        title={email.subject}
        subtitle={`To: ${email.recipientEmail}`}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" size="sm" onClick={() => navigate('/emails')}>← Back</Button>
            <Button variant="danger" size="sm" onClick={handleDelete}>Delete</Button>
          </div>
        }
      />
      <div style={{ padding: isMobile ? '16px' : '24px 32px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Email meta */}
        <Card style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Status</p>
              <Badge variant={statusVariant[email.status] || 'default'}>{email.status}</Badge>
            </div>
            <div>
              <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sent</p>
              <p style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)' }}>{formatDateTime(email.createdAt)}</p>
            </div>
            {email.firstOpenedAt && (
              <div>
                <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>First Opened</p>
                <p style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)', color: 'var(--cyan)' }}>{formatDateTime(email.firstOpenedAt)}</p>
              </div>
            )}
            {email.lastOpenedAt && (
              <div>
                <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Last Opened</p>
                <p style={{ fontSize: '0.82rem', fontFamily: 'var(--font-mono)' }}>{timeAgo(email.lastOpenedAt)}</p>
              </div>
            )}
            {email.tags?.length > 0 && (
              <div>
                <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Tags</p>
                <div style={{ display: 'flex', gap: 4 }}>{email.tags.map((t) => <Badge key={t}>#{t}</Badge>)}</div>
              </div>
            )}
          </div>
        </Card>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 16 }}>
          <StatCard label="Total Opens"  value={email.openCount}  accent="var(--cyan)"  icon="👁" />
          <StatCard label="Total Clicks" value={email.clickCount} accent="var(--amber)" icon="🔗" />
          <StatCard label="Tracked Links" value={email.trackedLinks?.length || 0} icon="🔗" />
          <StatCard label="Devices" value={deviceData.length} icon="📱" />
        </div>

        {/* Timeline Chart */}
        {chartData.length > 0 && (
          <Card style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 16 }}>Event Timeline</p>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="openGrad"  x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00e1c8" stopOpacity={0.3} /><stop offset="100%" stopColor="#00e1c8" stopOpacity={0} /></linearGradient>
                  <linearGradient id="clickGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f5a623" stopOpacity={0.3} /><stop offset="100%" stopColor="#f5a623" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#1e1e2e" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="time" tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: '#4a4a65', fontSize: 10, fontFamily: 'Space Mono' }} tickLine={false} axisLine={false} />
                <ReTooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="open"  stroke="#00e1c8" fill="url(#openGrad)"  strokeWidth={2} dot={false} name="Opens"  />
                <Area type="monotone" dataKey="click" stroke="#f5a623" fill="url(#clickGrad)" strokeWidth={2} dot={false} name="Clicks" />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: 20 }}>
          {/* Event Log */}
          <Card style={{ padding: '20px 24px' }}>
            <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
              Event Log ({allEvents.length})
            </p>
            {allEvents.length === 0 ? (
              <EmptyState icon="⏳" title="No events yet" description="Events will appear here when the email is opened or links are clicked" />
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {allEvents.map((ev, i) => <EventRow key={i} event={ev} />)}
              </div>
            )}
          </Card>

          {/* Tracked Links + Device Breakdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Tracked Links */}
            <Card style={{ padding: '20px 24px', flex: 1 }}>
              <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
                Tracked Links ({email.trackedLinks?.length || 0})
              </p>
              {email.trackedLinks?.length === 0 ? (
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>No links in this email</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {email.trackedLinks?.map((link) => (
                    <div key={link.linkId} style={{ padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                        <span style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>id:{link.linkId}</span>
                        <span style={{ fontSize: '0.8rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: link.clickCount > 0 ? 'var(--amber)' : 'var(--text-muted)' }}>{link.clickCount} clicks</span>
                      </div>
                      <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{truncateUrl(link.originalUrl, 40)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Device Breakdown */}
            {deviceData.length > 0 && (
              <Card style={{ padding: '20px 24px' }}>
                <p style={{ fontSize: '0.68rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>Devices</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {deviceData.map((d) => {
                    const total = deviceData.reduce((s, x) => s + x.count, 0);
                    const pctVal = Math.round((d.count / total) * 100);
                    return (
                      <div key={d.type}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{deviceIcon(d.type)} {d.type}</span>
                          <span style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{pctVal}%</span>
                        </div>
                        <div style={{ height: 4, background: 'var(--bg-elevated)', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pctVal}%`, background: 'var(--cyan)', borderRadius: 2, transition: 'width 600ms ease' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};
